import {
  Button,
  LinkButton,
  Select,
  Switch,
  Text,
  useToastContext,
} from "@ttc3k/trekker";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useSearchParams } from "react-router";
import { css } from "styled-system/css";
import { Flex } from "styled-system/jsx";
import { InlineSectionTitle } from "shared/components";
import { StickyHeader } from "../StickyHeader";
import { OrganizationSettingsViewKey } from "../../../useOrganizationSettings";
import {
  isBookerWiredForTier,
  useMembershipPayment,
  type MembershipCheckoutOption,
} from "./useMembershipPayment";
import { useCreateMembershipCheckout } from "./useCreateMembershipCheckout";
import { CreateMembershipCheckoutModal } from "./CreateMembershipCheckoutModal";

const NONE_VALUE = "__none__";
const FREE_VALUE = "__free__";

const linkClassName = css({
  color: "text.accent.purple.mid",
  fontWeight: "medium",
  _hover: { color: "text.accent.purple.dark" },
  transitionProperty: "color",
  transitionDuration: "normal",
});

const cardProps = {
  flexDir: "column",
  gap: "200",
  p: "300",
  borderWidth: "1px",
  borderColor: "border.light",
  borderRadius: "200",
  alignItems: "flex-start",
} as const;

type PaymentSectionProps = {
  organizationId?: string;
  savedTiers: string[];
  tagOptions: { value: string; label: string }[];
  membershipTranslationValues: Record<string, string>;
};

export const PaymentSection = ({
  organizationId,
  savedTiers,
  tagOptions,
  membershipTranslationValues,
}: PaymentSectionProps) => {
  const { t } = useTranslation("home", {
    keyPrefix: "VIEWS.SETTINGS.VIEWS.MEMBERSHIP_TIERS.PAYMENT",
  });
  const { t: tCore } = useTranslation("core");
  const { toastFactory } = useToastContext();
  const [searchParams, setSearchParams] = useSearchParams();

  const {
    loading,
    paymentsEnabled,
    setPaymentsEnabled,
    savingPaymentsEnabled,
    isStorefrontConnected,
    targetStorefrontId,
    targetIsTest,
    showCurrency,
    checkoutOptions,
    tierCheckouts,
    renewalSettings,
    connectedOperatorId,
    canCreateMembershipProduct,
    saveTierCheckout,
    saving,
  } = useMembershipPayment(organizationId);

  const associateBooker = useCallback(
    (tierTagId: string, bookerId: string) =>
      saveTierCheckout(tierTagId, { bookerId }),
    [saveTierCheckout],
  );
  const { createCheckout, creating, hasOwner } = useCreateMembershipCheckout({
    targetStorefrontId,
    connectedOperatorId,
    renewalSettings,
    onAssociate: associateBooker,
  });

  const [createForTierId, setCreateForTierId] = useState<string | null>(null);

  const tierLabel = useCallback(
    (tierTagId: string) =>
      tagOptions.find(o => o.value === tierTagId)?.label ?? tierTagId,
    [tagOptions],
  );

  const optionLabel = useCallback(
    (option: MembershipCheckoutOption) =>
      [
        option.label,
        option.isTest ? t("TEST_LABEL") : t("LIVE_LABEL"),
        showCurrency ? (option.isUsd ? "USD" : "CAD") : null,
      ]
        .filter(Boolean)
        .join(" · "),
    [t, showCurrency],
  );

  const selectItems = useMemo(
    () => [
      { value: NONE_VALUE, label: t("NO_WIDGET") },
      { value: FREE_VALUE, label: t("FREE_TIER") },
      ...checkoutOptions.map(option => ({
        value: option.value,
        label: optionLabel(option),
      })),
    ],
    [checkoutOptions, optionLabel, t],
  );
  const selectCollection = useMemo(
    () => Select.createCollection({ items: selectItems }),
    [selectItems],
  );

  const storefrontPath = (storefrontId: string, bookerId: string) =>
    `/app/organization/${organizationId}/storefront/${storefrontId}/${bookerId}`;

  const goToStorefrontSettings = () => {
    const next = new URLSearchParams(searchParams);
    next.set("settingsSubView", OrganizationSettingsViewKey.StorefrontSettings);
    setSearchParams(next);
  };

  const handleSelect = async (tierTagId: string, value: string | undefined) => {
    try {
      await saveTierCheckout(
        tierTagId,
        !value || value === NONE_VALUE
          ? null
          : value === FREE_VALUE
            ? { free: true }
            : { bookerId: value },
      );
      toastFactory.create({
        title: tCore("SUCCESS"),
        description: t("SAVE_SUCCESS"),
      });
    } catch {
      toastFactory.create({
        title: tCore("ERROR.TITLE"),
        description: t("SAVE_ERROR"),
      });
    }
  };

  const handleCreate = async ({
    name,
    price,
  }: {
    name: string;
    price: number;
  }) => {
    if (!createForTierId) return;
    try {
      await createCheckout({ tierTagId: createForTierId, name, price });
      setCreateForTierId(null);
      toastFactory.create({
        title: tCore("SUCCESS"),
        description: t("MODAL.SUCCESS"),
      });
    } catch {
      toastFactory.create({
        title: tCore("ERROR.TITLE"),
        description: t("MODAL.ERROR"),
      });
    }
  };

  const renderTierRow = (tierTagId: string) => {
    const checkout = tierCheckouts.get(tierTagId);
    const bookerId = checkout?.bookerId;
    const isFree = !!checkout?.free;
    const selectValue = isFree ? FREE_VALUE : (bookerId ?? NONE_VALUE);
    const selected = bookerId
      ? checkoutOptions.find(o => o.value === bookerId)
      : undefined;
    const tierName = tierLabel(tierTagId);

    return (
      <Flex
        key={tierTagId}
        flexDir="column"
        gap="150"
        py="200"
        borderBottomWidth="1px"
        borderColor="border.light"
        _last={{ borderBottomWidth: 0 }}
      >
        <Flex gap="300" alignItems="center">
          <Text visual="smallSemiBold" color="text.dark" w="160px" flexShrink={0}>
            {tierName}
          </Text>
          <Select.Root
            collection={selectCollection}
            value={[selectValue]}
            onValueChange={d => handleSelect(tierTagId, d.value[0])}
            disabled={saving || creating}
            style={{ flex: 1, minWidth: 0 }}
          >
            {selectItems.map(item => (
              <Select.Item key={item.value} item={item} />
            ))}
          </Select.Root>
        </Flex>
        <Flex pl="calc(160px + token(spacing.300))" flexDir="column" gap="100">
          {selected ? (
            <>
              <Link
                to={storefrontPath(
                  String(selected.booker.storefront),
                  selected.value,
                )}
                className={linkClassName}
              >
                <Text as="span" visual="smallMedium">
                  {t("OPEN_IN_STOREFRONT")}
                </Text>
              </Link>
              {!isBookerWiredForTier(selected.booker, tierTagId) && (
                <Text visual="smallRegular" color="text.warning.mid">
                  {t("NOT_WIRED_WARNING", { tier: tierName })}
                </Text>
              )}
            </>
          ) : bookerId ? (
            <Text visual="smallRegular" color="text.warning.mid">
              {t("MISSING_WIDGET")}
            </Text>
          ) : isFree ? (
            <Text visual="smallRegular" color="text.light">
              {t("FREE_TIER_HINT", membershipTranslationValues)}
            </Text>
          ) : (
            <Flex flexDir="column" gap="50" alignItems="flex-start">
              <LinkButton
                type="button"
                onClick={() => setCreateForTierId(tierTagId)}
                disabled={
                  !canCreateMembershipProduct || !hasOwner || creating
                }
              >
                {t("CREATE_WIDGET")}
              </LinkButton>
              {!canCreateMembershipProduct && (
                <Text visual="smallRegular" color="text.light">
                  {t("RENEWALS_REQUIRED", membershipTranslationValues)}
                </Text>
              )}
              {canCreateMembershipProduct && !hasOwner && !loading && (
                <Text visual="smallRegular" color="text.light">
                  {t("NO_OWNER")}
                </Text>
              )}
            </Flex>
          )}
        </Flex>
      </Flex>
    );
  };

  return (
    <Flex flexDir="column" gap="250" pt="300" w="full" alignSelf="flex-start">
      <StickyHeader>
        <InlineSectionTitle
          title={t("TITLE")}
          dividerProps={{ display: "none" }}
        />
      </StickyHeader>
      <Text visual="smallRegular" color="text.mid">
        {t("DESCRIPTION", membershipTranslationValues)}
      </Text>

      <Switch
        checked={paymentsEnabled}
        disabled={loading || savingPaymentsEnabled}
        onCheckedChange={async d => {
          try {
            await setPaymentsEnabled(d.checked);
          } catch {
            toastFactory.create({
              title: tCore("ERROR.TITLE"),
              description: t("ENABLED_SAVE_ERROR"),
            });
          }
        }}
        label={t("ENABLED", membershipTranslationValues)}
      />

      {!paymentsEnabled ? null : !loading && !isStorefrontConnected ? (
        <Flex {...cardProps}>
          <Text color="text.mid">
            {t("NOT_CONNECTED", membershipTranslationValues)}
          </Text>
          <Button
            visual="primary"
            mode="light"
            type="button"
            onClick={goToStorefrontSettings}
          >
            {t("ACTIVATE_STOREFRONT")}
          </Button>
        </Flex>
      ) : isStorefrontConnected && savedTiers.length === 0 ? (
        <Flex {...cardProps}>
          <Text color="text.mid">
            {t("NO_TIERS", membershipTranslationValues)}
          </Text>
        </Flex>
      ) : isStorefrontConnected ? (
        <Flex flexDir="column" gap="100">
          <Text visual="smallRegular" color="text.light">
            {t(
              targetIsTest ? "TEST_STOREFRONT_NOTE" : "LIVE_STOREFRONT_NOTE",
              membershipTranslationValues,
            )}
          </Text>
          <Flex flexDir="column">{savedTiers.map(renderTierRow)}</Flex>
        </Flex>
      ) : null}

      {createForTierId && (
        <CreateMembershipCheckoutModal
          isOpen={!!createForTierId}
          onClose={() => setCreateForTierId(null)}
          tierName={tierLabel(createForTierId)}
          defaultName={`${tierLabel(createForTierId)} ${membershipTranslationValues.membership ?? ""}`.trim()}
          targetIsTest={targetIsTest}
          loading={creating}
          onCreate={handleCreate}
          membershipTranslationValues={membershipTranslationValues}
        />
      )}
    </Flex>
  );
};
